/**
 * ChartReportModal - Modal for displaying AI-generated charts
 *
 * Displays various chart types (bar, line, pie) using Recharts.
 * Supports offering to email reports.
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { EnvelopeIcon, XIcon, DownloadIcon } from '@phosphor-icons/react';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { formatCurrency } from '@erp/shared-utils';
import { MarkdownWYSIWYGEditor } from '@/components/common/MarkdownWYSIWYGEditor';

export interface ChartData {
  id: string;
  title: string;
  description: string;
  chartType: 'bar' | 'line' | 'pie';
  data: Array<Record<string, unknown>>;
  xAxisLabel: string;
  yAxisLabel: string;
  color: string;
  secondaryColor?: string;
}

interface ChartReportModalProps {
  open: boolean;
  onClose: () => void;
  chart: ChartData | null;
  offerEmailReport?: boolean;
  onEmailReport?: () => void;
}

// Default pie colors if not specified
const PIE_COLORS = [
  '#22c55e',
  '#3b82f6',
  '#f59e0b',
  '#ef4444',
  '#8b5cf6',
  '#06b6d4',
  '#ec4899',
  '#84cc16',
];

// Custom tooltip formatter for currency values
const currencyFormatter = (value: number) => formatCurrency(value);

export function ChartReportModal({
  open,
  onClose,
  chart,
  offerEmailReport = false,
  onEmailReport,
}: ChartReportModalProps) {
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');

  // Reset email form when modal closes
  useEffect(() => {
    if (!open) {
      setShowEmailForm(false);
      setEmailTo('');
      setEmailSubject('');
      setEmailBody('');
    }
  }, [open]);

  if (!chart) {
    return null;
  }

  // TypeScript now knows chart is not null after the early return
  const chartData = chart;

  const handleEmailClick = () => {
    setShowEmailForm(true);
    // Set default subject based on chart title
    if (!emailSubject) {
      setEmailSubject(`Report: ${chartData.title}`);
    }
  };

  const handleSendEmail = () => {
    if (onEmailReport) {
      onEmailReport();
    }
    setShowEmailForm(false);
    setEmailTo('');
    setEmailSubject('');
    setEmailBody('');
  };

  const handleCancelEmail = () => {
    setShowEmailForm(false);
    setEmailTo('');
    setEmailSubject('');
    setEmailBody('');
  };

  const renderChart = () => {
    switch (chartData.chartType) {
      case 'bar':
        return renderBarChart();
      case 'line':
        return renderLineChart();
      case 'pie':
        return renderPieChart();
      default:
        return <div>Unsupported chart type</div>;
    }
  };

  const renderBarChart = () => {
    // Detect data keys (excluding 'name')
    const dataKeys = Object.keys(chartData.data[0] || {}).filter(
      (key) => key !== 'name',
    );

    return (
      <ResponsiveContainer width="100%" height={400}>
        <BarChart
          data={chartData.data}
          margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis
            dataKey="name"
            tick={{ fontSize: 11 }}
            angle={-45}
            textAnchor="end"
            height={80}
            interval={0}
          />
          <YAxis tickFormatter={currencyFormatter} tick={{ fontSize: 11 }} />
          <Tooltip
            formatter={(value) => [
              typeof value === 'number' ? formatCurrency(value) : String(value),
              '',
            ]}
            contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            }}
          />
          {dataKeys.map((key, index) => (
            <Bar
              key={key}
              dataKey={key}
              name={formatKeyName(key)}
              fill={
                index === 0
                  ? chartData.color
                  : chartData.secondaryColor || chartData.color
              }
              radius={[4, 4, 0, 0]}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    );
  };

  const renderLineChart = () => {
    // Detect data keys (excluding 'name')
    const dataKeys = Object.keys(chartData.data[0] || {}).filter(
      (key) => key !== 'name',
    );

    return (
      <ResponsiveContainer width="100%" height={400}>
        <LineChart
          data={chartData.data}
          margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis dataKey="name" tick={{ fontSize: 12 }} />
          <YAxis tickFormatter={currencyFormatter} tick={{ fontSize: 11 }} />
          <Tooltip
            formatter={(value) => [
              typeof value === 'number' ? formatCurrency(value) : 'N/A',
              '',
            ]}
            contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            }}
          />
          {dataKeys.map((key, index) => (
            <Line
              key={key}
              type="monotone"
              dataKey={key}
              name={formatKeyName(key)}
              stroke={
                index === 0
                  ? chartData.color
                  : chartData.secondaryColor || chartData.color
              }
              strokeWidth={2}
              dot={{ r: 4 }}
              connectNulls={false}
              strokeDasharray={key === 'projected' ? '5 5' : undefined}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    );
  };

  const renderPieChart = () => {
    return (
      <ResponsiveContainer width="100%" height={400}>
        <PieChart>
          <Pie
            data={chartData.data}
            cx="50%"
            cy="50%"
            labelLine={true}
            label={({ name, percent }) =>
              `${name}: ${(percent * 100).toFixed(0)}%`
            }
            outerRadius={140}
            fill="#8884d8"
            dataKey="value"
          >
            {chartData.data.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={
                  (entry as { color?: string }).color ||
                  PIE_COLORS[index % PIE_COLORS.length]
                }
              />
            ))}
          </Pie>
          <Tooltip
            formatter={(value) => [
              typeof value === 'number' ? formatCurrency(value) : String(value),
              '',
            ]}
            contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            }}
          />
        </PieChart>
      </ResponsiveContainer>
    );
  };

  // Helper to format camelCase keys to readable labels
  const formatKeyName = (key: string): string => {
    return key
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (str) => str.toUpperCase())
      .replace(/year(\d+)/i, 'Year $1');
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className={`max-h-[90vh] overflow-y-auto ${
          showEmailForm ? 'max-w-7xl sm:max-w-7xl' : 'max-w-4xl sm:max-w-4xl'
        }`}
      >
        <DialogHeader>
          <DialogTitle className="text-xl">{chartData.title}</DialogTitle>
          <DialogDescription>{chartData.description}</DialogDescription>
        </DialogHeader>

        <div
          className={`py-4 ${
            showEmailForm ? 'grid grid-cols-[1.2fr_1fr] gap-8' : ''
          }`}
        >
          <div className={showEmailForm ? 'min-w-0' : ''}>{renderChart()}</div>

          {showEmailForm && (
            <div className="space-y-4 border-l pl-6">
              <div className="space-y-2">
                <Label htmlFor="email-to">To</Label>
                <Input
                  id="email-to"
                  value={emailTo}
                  onChange={(e) => setEmailTo(e.target.value)}
                  placeholder="Enter email address"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email-subject">Subject</Label>
                <Input
                  id="email-subject"
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  placeholder="Enter email subject"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email-body">Message</Label>
                <MarkdownWYSIWYGEditor
                  initialValue={emailBody}
                  onChange={setEmailBody}
                  placeholder="Write your message..."
                  height={300}
                  showToolbar={true}
                />
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="flex gap-2 sm:justify-between">
          <div className="flex gap-2">
            {offerEmailReport && onEmailReport && (
              <>
                {!showEmailForm ? (
                  <Button variant="default" onClick={handleEmailClick}>
                    <EnvelopeIcon className="h-4 w-4 mr-2" />
                    Email
                  </Button>
                ) : (
                  <>
                    <Button variant="outline" onClick={handleCancelEmail}>
                      Cancel
                    </Button>
                    <Button
                      variant="default"
                      onClick={handleSendEmail}
                      disabled={!emailTo || !emailSubject}
                    >
                      <EnvelopeIcon className="h-4 w-4 mr-2" />
                      Send Email
                    </Button>
                  </>
                )}
              </>
            )}
          </div>
          <Button variant="outline" onClick={onClose}>
            <XIcon className="h-4 w-4 mr-2" />
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ChartReportModal;
